import { head, isEmpty } from 'lodash-es';
import {
    BehaviorSubject,
    Observable,
    distinctUntilChanged,
    shareReplay,
    pipe,
    switchMap,
    defer,
    tap,
    map,
    timer,
    take,
    startWith,
    identity,
    catchError,
    of,
    Subject,
    from,
    finalize,
    throwError,
    mergeMap,
    retry,
    combineLatest,
} from 'rxjs';
import { ExtendableError } from './errors';

// Operator that switches to the first Observable in the incoming array of Observables.
// Very useful to use in conjunction with the `useObservable` hook when the dependency
// array consists of only one observable.
export function switchHead() {
    return pipe(switchMap(head));
}

export function distinctUntilNotEmptyAnymore() {
    return pipe(distinctUntilChanged((previous, current) => isEmpty(previous) && isEmpty(current)));
}

const defaultFromObservableQueryOptions = {
    isLoading$: null,
    error$: null,
    useInitialLoading: false,
};

// Source: https://github.com/kamilkisiela/apollo-angular/issues/835#issuecomment-456332352
export function fromObservableQuery(observableQuery, optionsWithoutDefaults) {
    const options = {
        ...defaultFromObservableQueryOptions,
        ...optionsWithoutDefaults,
    };

    const observable = new Observable((subscriber) => {
        const subscription = observableQuery.subscribe(
            (value) => {
                subscriber.next(value);
            },
            (error) => {
                subscriber.error(error);
            },
            () => subscriber.complete()
        );

        return () => {
            subscription.unsubscribe();
        };
    });

    return observable.pipe(
        // Note: There is a bug in `watchQuery` that prevents emitting the initial
        // loading state (e.g., `loading: true`). Instead, due to this bug, the
        // only state sent is `loading: false` when the request completes. This
        // issue is also observed in Apollo Angular packages and is addressed
        // here:
        // https://github.com/kamilkisiela/apollo-angular/blob/f3dc3656a29e20172a8c25fa39ffc6da52b278d3/packages/apollo-angular/src/query-ref.ts#L30-L39
        options.useInitialLoading
            ? startWith({
                  ...observableQuery.getCurrentResult(false),
                  error: undefined,
                  partial: undefined,
                  stale: true,
              })
            : identity,
        tap({
            next: (response) => {
                // Whenever the observableQuery emits a new value, we also update
                // the loading state using isLoading$. This allows consumers of
                // isLoading$ to react to loading state changes in real-time.
                if (options.isLoading$ instanceof Subject) {
                    options.isLoading$.next(response.loading);
                }
            },
            subscribe: () => {
                // Clear any previous errors in error$.
                if (options.error$ instanceof Subject) {
                    options.error$.next();
                }
            },
            finalize: () => {
                // Ensure to set loading to false when the operation finalizes.
                if (options.isLoading$ instanceof Subject) {
                    options.isLoading$.next(false);
                }
            },
        }),
        // eslint-disable-next-line rxjs/no-implicit-any-catch
        catchError((err) => {
            if (options.error$ instanceof Subject) {
                // If an error subject is supplied, the error will be passed to it,
                // and `null` will be returned from this observable.
                options.error$.next(err);
                return of(null);
            }

            throw err;
        })
    );
}

/**
 * Creates an observable for executing a GraphQL mutation via Apollo client.
 * Optionally manages a loading and error state through provided BehaviorSubjects.
 *
 * @param {ApolloClient} client - The Apollo client instance to use for the mutation.
 * @param {Object} mutateArgs - Arguments to pass to the client's mutate method.
 * @param {BehaviorSubject<boolean>} isLoading$ - Optional. BehaviorSubject to manage
 *        the loading state. It emits true when the mutation starts and false when it
 *        either completes or an error occurs.
 * @param {BehaviorSubject<any>} error$ - Optional. BehaviorSubject to manage
 *        the error state. It emits an error object when an error occurs during the mutation.
 * @return {Observable} - An observable that triggers the mutation and handles errors.
 *        If an error occurs and error$ is provided, the observable will return `false`,
 *        otherwise it rethrows the error to be handled by further subscriptions.
 */
export function createMutationObservable(client, mutateArgs, isLoading$ = null, error$ = null) {
    return from(client.mutate(mutateArgs)).pipe(
        tap({
            subscribe: () => {
                if (isLoading$) {
                    isLoading$.next(true);
                }
                if (error$) {
                    error$.next();
                }
            },
            finalize: () => {
                if (isLoading$) {
                    isLoading$.next(false);
                }
            },
        }),
        // eslint-disable-next-line rxjs/no-implicit-any-catch
        catchError((err) => {
            if (error$) {
                // If an error subject is supplied, the error will be passed to it,
                // and `null` will be returned from this observable.
                error$.next(err);
                return of(null);
            }

            throw err;
        })
    );
}

export function shareReplayRefCount() {
    return pipe(shareReplay({ bufferSize: 1, refCount: true }));
}

export class Collection {
    constructor() {
        this._entries$ = new BehaviorSubject([]);
    }

    // Making entries$ accessible through a getter method enhances the flexibility
    // of our class. It sets the ground for extensions via inheritance. For instance,
    // you could add additional functionality that is triggered when entries$ is
    // subscribed to, as well as cleanup logic for when it is unsubscribed from.
    get entries$() {
        return this._entries$;
    }

    add(entry) {
        this._entries$.next(this._entries$.getValue().concat([entry]));
        return entry;
    }

    delete(entry) {
        this._entries$.next(this._entries$.getValue().filter((value) => value !== entry));
    }

    clear() {
        this._entries$.next([]);
    }
}

export function timeFrames(frameApproximateLength, numberOfFrames) {
    return defer(() => {
        const clock = timer(0, frameApproximateLength);
        if (numberOfFrames) {
            clock.pipe(take(numberOfFrames));
        }
        let t0 = Date.now();
        let t1;
        return clock.pipe(
            tap(() => {
                t1 = Date.now();
            }),
            map(() => t1 - t0),
            tap(() => {
                t0 = t1;
            })
        );
    });
}

/**
 * Creates an observable that starts the provided `sideEffect$` observable
 * when the source observable is subscribed to, and automatically unsubscribes
 * from the `sideEffect$` when the source observable is unsubscribed from,
 * completes, or errors.
 *
 * The side effect observable is treated independently in terms of error handling
 * and completion. Errors or completions from `sideEffect$` do not affect the
 * source observable's stream.
 *
 * Usage of `defer` ensures that the side effect starts exactly at the time of
 * subscription to the returned observable, preventing any premature side effects.
 *
 * @param {Observable} sideEffect$ - The observable to start as a side effect.
 * @returns {Observable} A function that takes the source observable and returns
 *     it with the side effect applied.
 *
 * @example
 * const source$ = of(1, 2, 3);
 * const logging$ = of('Logging started').pipe(tap(console.log));
 * const enhancedObservable = source$.pipe(withSideEffect(logging$));
 * enhancedObservable.subscribe(value => console.log(`Received: ${value}`));
 */
export function withSideEffect(sideEffect$) {
    return (source) =>
        defer(() => {
            const sideEffectSubscription = sideEffect$.subscribe();

            return source.pipe(
                finalize(() => {
                    // Clean up the sideEffect$ subscription when the source completes or errors,
                    // or when the subscription to the resultant observable is cancelled.
                    sideEffectSubscription.unsubscribe();
                })
            );
        });
}

export function filterOnPropertyObservable(collection, property) {
    return combineLatest(
        collection.map((entry) => entry[property].pipe(map((result) => [result, entry])))
    ).pipe(
        map((results) =>
            results.filter(([result, _entry]) => result).map(([_result, entry]) => entry)
        )
    );
}

class ConditionNotMetForPollingError extends ExtendableError {
    constructor(message = 'Condition not met for polling after all attempts.') {
        super(message);
    }
}

/**
 * Polls a condition function until it returns true, or until the maximum total attempts are
 * exhausted. This function can use a fixed delay or an exponential backoff strategy for retries.
 *
 * @param {function(): boolean} conditionFn A synchronous function that returns `true` if
 * the condition is met, or `false` otherwise.
 * @param {number} [maxTotalAttempts=5] The total number of attempts to check the condition
 * (this includes the initial check plus subsequent retries). Must be 1 or greater.
 * If less than 1, the returned Observable will immediately emit a RangeError.
 * @param {number} [attemptIntervalMs=50] The base delay in milliseconds for retry attempts.
 * If `withBackoff` is `false`, this is the fixed delay used for all retries.
 * If `withBackoff` is `true`, this is the initial delay for the first retry,
 * and subsequent retries will use exponentially increasing delays
 * (e.g., D, 2*D, 4*D, ... where D is `attemptIntervalMs`).
 * @param {boolean} [withBackoff=true] If `true` (the default), an exponential backoff strategy
 * is applied to the delay between retries. If `false`, a fixed delay (specified by
 * `attemptIntervalMs`) is used for all retries.
 * @returns {Observable<boolean>} An Observable that emits a single boolean value:
 * `true` if the condition becomes true within the allowed attempts, or
 * `false` if the condition remains false after all attempts have been exhausted
 * (by catching a ConditionNotMetForPollingError and emitting false).
 * The Observable completes after emitting this boolean value.
 * If `maxTotalAttempts` is invalid, the Observable will emit a RangeError.
 */
export function pollConditionForResult(
    conditionFn,
    maxTotalAttempts = 5,
    attemptIntervalMs = 50,
    withBackoff = true
) {
    if (maxTotalAttempts < 1) {
        // If maxTotalAttempts is invalid, return an Observable that immediately errors.
        return throwError(() => new RangeError('maxTotalAttempts must be 1 or greater to poll.'));
    }

    return defer(() =>
        // This `defer` ensures `conditionFn` is called only when the Observable is subscribed to,
        // performing the initial attempt.
        of(conditionFn())
    ).pipe(
        mergeMap((isApplicable) => {
            if (isApplicable) {
                // If the condition is met, emit `true`, and this stream segment completes
                // successfully.
                return of(true);
            }
            // If the condition is not met, throw an instance of our custom error to trigger the
            // retry logic.
            return throwError(() => new ConditionNotMetForPollingError());
        }),
        retry({
            // This specifies the number of retries *after* the initial attempt fails.
            count: maxTotalAttempts - 1,
            // The `delay` function determines the wait time before each retry.
            // The first argument `_` is the error that caused the retry (not used in this delay
            // calculation).
            // The second argument `retryAttempt` is a 1-based count of the current retry attempt
            // (e.g., 1 for the first retry, 2 for the second, and so on).
            delay: (_, retryAttempt) => {
                const delayDuration = withBackoff
                    ? // If `withBackoff` is true, calculate delay using exponential backoff: D,
                      // 2*D, 4*D, ...
                      attemptIntervalMs * 2 ** (retryAttempt - 1)
                    : // Otherwise, use a fixed delay.
                      attemptIntervalMs;
                return timer(delayDuration);
            },
        }),
        // This `catchError` operator handles errors after all retries (and the initial attempt)
        // have failed.
        catchError((error) => {
            // Check if the error is an instance of our custom error.
            if (error instanceof ConditionNotMetForPollingError) {
                // Emit `false` to indicate final failure after all attempts because the condition
                // was never met.
                return of(false);
            }
            // If it's a different error (e.g., an unexpected error from `conditionFn` itself),
            // re-throw it.
            return throwError(() => error);
        }),
        // This operator ensures that the observable emits at most one value (true or false from the
        // logic above) and then completes.
        take(1)
    );
}
